import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

export type ContentWidthBand = "mobile" | "tablet" | "narrowDesktop" | "desktop";

/**
 * Viewport band helpers for AppShell pages (sidebar reduces usable content width).
 * - mobile: below sm
 * - tablet: sm .. lg-1
 * - narrowDesktop: lg .. xl-1 (125–150% scaling often lands here)
 * - desktop: xl+
 */
export function useContentWidthBand(): ContentWidthBand {
  const theme = useMantineTheme();
  const belowSm = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    true,
    { getInitialValueInEffect: true },
  );
  const belowLg = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.lg} - 0.0625em))`,
    true,
    { getInitialValueInEffect: true },
  );
  const belowXl = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.xl} - 0.0625em))`,
    true,
    { getInitialValueInEffect: true },
  );

  if (belowSm) return "mobile";
  if (belowLg) return "tablet";
  if (belowXl) return "narrowDesktop";
  return "desktop";
}

/** Tablet / iPad band: at or above `sm`, below `lg`. */
export function useTabletViewport() {
  const theme = useMantineTheme();
  return useMediaQuery(
    `(min-width: ${theme.breakpoints.sm}) and (max-width: calc(${theme.breakpoints.lg} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );
}

/** Narrow desktop: lg .. xl-1 (common at 125–150% OS scaling). */
export function useNarrowDesktopViewport() {
  const theme = useMantineTheme();
  return useMediaQuery(
    `(min-width: ${theme.breakpoints.lg}) and (max-width: calc(${theme.breakpoints.xl} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );
}
